/**
 * Cartoon drawing vocabulary (Canvas 2D), after Ghost Circuit's toon-fx:
 * ink outlines, flat cel shading (a lit body shifted up-left over a shade crescent),
 * a glint on the upper left, puffs, four-point sparkles, spiky starbursts and cute faces.
 *
 * Every helper draws in the caller's current transform; sizes are in that space.
 */
import {
  INK, LINE_BODY, LINE_DETAIL, SHADE_SHIFT, GLINT_AT, BLUSH, EYE_WHITE, PUFF_TONES,
} from "./palette.js";

var TAU = Math.PI * 2;

/** Deterministic 0–1 hash. */
export function hash(n) {
  var s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Small seeded RNG (mulberry32). */
export function seededRandom(seed) {
  var a = (seed >>> 0) || 1;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    var t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Round joins/caps and the ink colour for an outline of width `w`. */
export function inkStyle(c, w, color) {
  c.lineJoin = "round";
  c.lineCap = "round";
  c.lineWidth = w;
  c.strokeStyle = color || INK;
}

/**
 * Cel-shade a closed shape. `path(c, grow)` must add the shape to a fresh path, grown
 * outward by `grow` (0 = the shape itself). Paints: shade fill, lit body shifted up-left,
 * optional light patch, then the ink outline of width `r * LINE_BODY` (or `opts.line`).
 *
 * @param {CanvasRenderingContext2D} c
 * @param {(c: CanvasRenderingContext2D, grow: number) => void} path
 * @param {{ base: string, light?: string, shade: string }} tone
 * @param {number} r subject radius (sets line weight and shade offset)
 * @param {{ line?: number, ink?: string, shift?: number[], light?: boolean, lightPath?: (c: CanvasRenderingContext2D) => void, noOutline?: boolean }} [opts]
 */
export function celShape(c, path, tone, r, opts) {
  var o = opts || {};
  var shift = o.shift || SHADE_SHIFT;
  c.save();
  c.beginPath();
  path(c, 0);
  c.fillStyle = tone.shade;
  c.fill();
  c.clip();
  c.translate(shift[0] * r, shift[1] * r);
  c.beginPath();
  path(c, -r * 0.02);
  c.fillStyle = tone.base;
  c.fill();
  c.restore();
  if (o.lightPath && tone.light) {
    c.save();
    c.beginPath();
    path(c, 0);
    c.clip();
    c.beginPath();
    o.lightPath(c);
    c.fillStyle = tone.light;
    c.fill();
    c.restore();
  }
  if (!o.noOutline) {
    c.beginPath();
    path(c, 0);
    inkStyle(c, o.line != null ? o.line : r * LINE_BODY, o.ink);
    c.stroke();
  }
}

/** Cel-shaded ellipse with an ink edge and an optional glint. */
export function celEllipse(c, x, y, rx, ry, tone, opts) {
  var o = opts || {};
  var r = Math.max(rx, ry);
  celShape(c, function (cc, g) {
    cc.ellipse(x, y, Math.max(0.1, rx + g), Math.max(0.1, ry + g), o.rot || 0, 0, TAU);
  }, tone, r, o);
  if (o.glint !== false && tone.light) {
    glint(c, x + GLINT_AT[0] * rx, y + GLINT_AT[1] * ry, r * 0.16, o.glintColor);
  }
}

/** A soft white glint: a rounded tick plus a dot, like a toy highlight. */
export function glint(c, x, y, s, color) {
  c.save();
  c.fillStyle = color || "rgba(255,255,255,0.92)";
  c.beginPath();
  c.ellipse(x, y, s * 0.9, s * 0.55, -0.7, 0, TAU);
  c.fill();
  c.beginPath();
  c.arc(x + s * 1.25, y - s * 0.55, s * 0.32, 0, TAU);
  c.fill();
  c.restore();
}

/** Soft contact shadow ellipse under a standing subject. */
export function contactShadow(c, x, y, rx, ry, alpha) {
  c.save();
  c.fillStyle = "rgba(10,30,50," + (alpha != null ? alpha : 0.22) + ")";
  c.beginPath();
  c.ellipse(x, y, rx, ry, 0, 0, TAU);
  c.fill();
  c.restore();
}

/**
 * A cute face centred on (x, y). `s` is the face scale (≈ the head radius).
 * mood: "happy" | "smile" | "wow" | "grin" | "sleepy" | "cool" | "fierce" | "blink"
 */
export function cuteFace(c, x, y, s, opts) {
  var o = opts || {};
  var mood = o.mood || "smile";
  var gap = s * (o.gap != null ? o.gap : 0.42);
  var eyeR = s * (o.eye != null ? o.eye : 0.17);
  var eyeY = y - s * (o.eyeLift != null ? o.eyeLift : 0.05);
  var look = o.look || [0, 0];
  var ink = o.ink || INK;
  inkStyle(c, Math.max(1, s * LINE_DETAIL * 1.6), ink);

  for (var side = -1; side <= 1; side += 2) {
    var ex = x + side * gap * 0.5;
    if (mood === "blink" || mood === "sleepy" || mood === "happy") {
      // Closed eyes: arcs (happy curves up, sleepy curves down).
      c.beginPath();
      if (mood === "happy") c.arc(ex, eyeY + eyeR * 0.35, eyeR * 0.85, Math.PI * 1.15, Math.PI * 1.85);
      else c.arc(ex, eyeY - eyeR * 0.2, eyeR * 0.8, Math.PI * 0.15, Math.PI * 0.85);
      c.stroke();
    } else {
      var ry = eyeR * (mood === "wow" ? 1.25 : 1.18);
      c.fillStyle = ink;
      c.beginPath();
      c.ellipse(ex + look[0] * eyeR * 0.3, eyeY + look[1] * eyeR * 0.3, eyeR, ry, 0, 0, TAU);
      c.fill();
      // Two highlights: a big one up-left, a little one down-right.
      c.fillStyle = EYE_WHITE;
      c.beginPath();
      c.arc(ex - eyeR * 0.32 + look[0] * eyeR * 0.3, eyeY - ry * 0.38 + look[1] * eyeR * 0.3, eyeR * 0.42, 0, TAU);
      c.fill();
      c.beginPath();
      c.arc(ex + eyeR * 0.36 + look[0] * eyeR * 0.3, eyeY + ry * 0.4 + look[1] * eyeR * 0.3, eyeR * 0.18, 0, TAU);
      c.fill();
      if (mood === "fierce" || mood === "cool") {
        // A slanted brow over each eye: the inner end dips toward the nose (cross for
        // "fierce", a smug tilt for "cool"); an inner end raised above the outer reads worried.
        c.beginPath();
        c.moveTo(ex - side * eyeR * 1.2, eyeY - ry * (mood === "fierce" ? 1.05 : 1.35));
        c.lineTo(ex + side * eyeR * 0.9, eyeY - ry * 1.6);
        c.stroke();
      }
    }
  }

  if (o.blush !== false) {
    c.save();
    c.fillStyle = o.blushColor || BLUSH;
    c.globalAlpha = 0.55;
    for (var b = -1; b <= 1; b += 2) {
      c.beginPath();
      c.ellipse(x + b * gap * 0.92, eyeY + eyeR * 1.55, eyeR * 0.85, eyeR * 0.5, 0, 0, TAU);
      c.fill();
    }
    c.restore();
  }

  var my = eyeY + eyeR * (o.mouthDrop != null ? o.mouthDrop : 1.55);
  var mw = s * (o.mouth != null ? o.mouth : 0.16);
  c.beginPath();
  if (mood === "wow") {
    c.fillStyle = "#7a2a3a";
    c.ellipse(x, my + mw * 0.3, mw * 0.55, mw * 0.7, 0, 0, TAU);
    c.fill();
    c.stroke();
  } else if (mood === "grin" || mood === "happy") {
    // Open smile with a tongue.
    c.moveTo(x - mw, my - mw * 0.1);
    c.quadraticCurveTo(x, my + mw * 1.6, x + mw, my - mw * 0.1);
    c.closePath();
    c.fillStyle = "#7a2a3a";
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = "#ff7a8a";
    c.beginPath();
    c.ellipse(x, my + mw * 0.85, mw * 0.55, mw * 0.4, 0, 0, TAU);
    c.fill();
    c.restore();
    c.beginPath();
    c.moveTo(x - mw, my - mw * 0.1);
    c.quadraticCurveTo(x, my + mw * 1.6, x + mw, my - mw * 0.1);
    c.closePath();
    c.stroke();
  } else if (mood === "fierce") {
    c.moveTo(x - mw * 0.8, my + mw * 0.2);
    c.quadraticCurveTo(x, my - mw * 0.25, x + mw * 0.8, my + mw * 0.2);
    c.stroke();
  } else {
    // "w" cat-smile for smile/cool/blink/sleepy.
    c.moveTo(x - mw, my);
    c.quadraticCurveTo(x - mw * 0.5, my + mw * 0.75, x, my + mw * 0.05);
    c.quadraticCurveTo(x + mw * 0.5, my + mw * 0.75, x + mw, my);
    c.stroke();
  }
}

/** Adds a four-point sparkle (concave sides) to the current path. Fill it. */
export function sparklePath(c, x, y, r, rot) {
  if (!(r > 0)) return;
  var a = rot || 0;
  var ca = Math.cos(a) * r, sa = Math.sin(a) * r;
  function p(u, v) { return [x + u * ca - v * sa, y + u * sa + v * ca]; }
  var A = p(1, 0), B = p(0, 1), C = p(-1, 0), D = p(0, -1);
  c.moveTo(A[0], A[1]);
  c.quadraticCurveTo(x, y, B[0], B[1]);
  c.quadraticCurveTo(x, y, C[0], C[1]);
  c.quadraticCurveTo(x, y, D[0], D[1]);
  c.quadraticCurveTo(x, y, A[0], A[1]);
  c.closePath();
}

/** A spiky starburst path: `n` points between `inner`·r and r, jittered by `seed`. */
export function starburstPath(c, x, y, r, opts) {
  var o = opts || {};
  var n = o.n || 10, inner = o.inner != null ? o.inner : 0.52, seed = o.seed || 0;
  var rot = o.rot || 0, squash = o.squash != null ? o.squash : 1;
  c.beginPath();
  for (var i = 0; i < n * 2; i++) {
    var a = rot + (i / (n * 2)) * TAU;
    var jag = i % 2 ? inner * (0.9 + 0.2 * hash(seed + i)) : 0.82 + 0.3 * hash(seed + i * 3 + 1);
    var d = r * jag;
    var px = x + Math.cos(a) * d, py = y + Math.sin(a) * d * squash;
    if (i) c.lineTo(px, py); else c.moveTo(px, py);
  }
  c.closePath();
}

/** A flame tongue: round foot of radius `r` at (x, y), licking up `h`, tip swayed by `sway`. */
export function flamePath(c, x, y, r, h, sway) {
  var sw = sway || 0;
  c.moveTo(x - r, y);
  c.bezierCurveTo(x - r, y - h * 0.45, x + sw - r * 0.2, y - h * 0.7, x + sw, y - h);
  c.bezierCurveTo(x + sw + r * 0.2, y - h * 0.7, x + r, y - h * 0.45, x + r, y);
  c.arc(x, y, r, 0, Math.PI);
  c.closePath();
}

/** Puff shapes in unit space: lumpy clouds a little under radius 1, [x, y, r] per lobe. */
export var PUFF_SHAPES = [
  [[0, 0.12, 0.62], [-0.44, 0.2, 0.42], [0.44, 0.22, 0.44], [-0.14, -0.3, 0.48], [0.3, -0.2, 0.4]],
  [[0, 0.08, 0.6], [-0.5, 0.12, 0.38], [0.48, 0.08, 0.42], [0.06, -0.36, 0.46], [-0.3, -0.22, 0.36]],
  [[0.04, 0.14, 0.58], [-0.4, 0.26, 0.4], [0.46, 0.3, 0.36], [-0.24, -0.26, 0.44], [0.26, -0.3, 0.42], [0.56, -0.04, 0.3]],
];

function lobes(c, shape, grow, dx, dy, scale) {
  c.beginPath();
  for (var i = 0; i < shape.length; i++) {
    var l = shape[i];
    var rr = Math.max(0, l[2] * scale + grow);
    c.moveTo(l[0] + dx + rr, l[1] + dy);
    c.arc(l[0] + dx, l[1] + dy, rr, 0, TAU);
  }
}

/** Paint a unit-space puff (ink rim, shade, lit body shifted up-left, light on top lobes). */
export function paintPuff(c, tone, shape) {
  var P = tone || PUFF_TONES.smoke;
  lobes(c, shape, 0.1, 0, 0, 1);
  c.fillStyle = P.ink;
  c.fill();
  c.save();
  lobes(c, shape, 0, 0, 0, 1);
  c.clip();
  c.fillStyle = P.shade;
  c.fillRect(-1.3, -1.3, 2.6, 2.6);
  lobes(c, shape, 0, -0.1, -0.13, 0.94);
  c.fillStyle = P.base;
  c.fill();
  lobes(c, shape.slice(3), 0, -0.2, -0.28, 0.62);
  c.fillStyle = P.light;
  c.fill();
  c.restore();
}

/** Ink-stroked text with a soft drop, for canvas-drawn labels. */
export function inkText(c, text, x, y, size, fill, opts) {
  var o = opts || {};
  c.save();
  c.font = (o.weight || 700) + " " + size + "px " + (o.family || "Fredoka, 'Baloo 2', system-ui, sans-serif");
  c.textAlign = o.align || "center";
  c.textBaseline = o.baseline || "middle";
  c.lineJoin = "round";
  c.fillStyle = "rgba(10,30,50,0.35)";
  c.fillText(text, x, y + size * 0.1);
  c.lineWidth = size * (o.stroke != null ? o.stroke : 0.2);
  c.strokeStyle = o.ink || INK;
  c.strokeText(text, x, y);
  c.fillStyle = fill;
  c.fillText(text, x, y);
  c.restore();
}

export { TAU, LINE_BODY, LINE_DETAIL };
